// frontend/src/components/Menu.js
import React, { useState, useEffect } from 'react';
import { Trash2, Edit, Plus, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { buttonPrimary, buttonSecondary, focusRing, swalConfirmColor, swalCancelColor } from '../theme';

const API_URL = '/api';

const Menu = ({ user }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [formData, setFormData] = useState({
    category: '',
    dish_name: '',
    status: 'On',
    description: '',
    tags: [],
    spice_level: 'Mild',
    cost: ''
  });

  const availableTags = ['Vegan', 'Vegetarian', 'Gluten Free', 'Dairy Free', 'Nut Free'];
  const spiceLevels = ['Mild', 'Medium', 'Hot'];

  useEffect(() => {
    if (initialLoad) {
      const timer = setTimeout(() => {
        fetchMenuItems();
        fetchCategories();
        setInitialLoad(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [initialLoad]);

  useEffect(() => {
    if (user && !initialLoad) {
      fetchMenuItems();
      fetchCategories();
    }
  }, [user]);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/menu`);
      setMenuItems(response.data);
    } catch (err) {
      console.error('Error fetching menu items:', err);
      toast.error('Failed to fetch menu items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      toast.error('Failed to fetch categories');
    }
  };

  const handleSubmit = async () => {
    // For users: only allow status updates
    if (user.role !== 'admin') {
      if (!editingItem) {
        toast.error('You do not have permission to add new dishes');
        return;
      }
      
      try {
        setLoading(true);
        // Only update status for non-admin users
        await axios.put(`${API_URL}/menu/${editingItem.id}`, {
          ...editingItem,
          status: formData.status
        });
        toast.success('Status updated successfully!');
        await fetchMenuItems();
        resetForm();
      } catch (err) {
        console.error('Error updating status:', err);
        toast.error('Failed to update status');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Admin: full permissions
    if (!formData.category || !formData.dish_name || !formData.description || !formData.cost) {
      toast.warning('Please fill in all required fields');
      return;
    }
    
    try {
      setLoading(true);
      if (editingItem) {
        await axios.put(`${API_URL}/menu/${editingItem.id}`, formData);
        toast.success('Dish updated successfully!');
      } else {
        await axios.post(`${API_URL}/menu`, formData);
        toast.success('Dish added successfully!');
      }
      await fetchMenuItems();
      await fetchCategories();
      resetForm();
    } catch (err) {
      console.error('Error saving menu item:', err);
      toast.error(err.response?.data?.message || 'Failed to save dish');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Dish?',
      text: "This will remove the dish from your menu",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalConfirmColor,
      cancelButtonColor: swalCancelColor,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        popup: 'rounded-lg',
        confirmButton: 'px-6 py-3 rounded-md font-medium',
        cancelButton: 'px-6 py-3 rounded-md font-medium'
      }
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await axios.delete(`${API_URL}/menu/${id}`);
        await fetchMenuItems();
        toast.success('Dish deleted successfully!');
      } catch (err) {
        console.error('Error deleting menu item:', err);
        toast.error('Failed to delete dish');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      category: item.category,
      dish_name: item.dish_name,
      status: item.status,
      description: item.description,
      tags: item.tags || [],
      spice_level: item.spice_level,
      cost: item.cost.toString()
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      category: '',
      dish_name: '',
      status: 'On',
      description: '',
      tags: [],
      spice_level: 'Mild',
      cost: ''
    });
    setEditingItem(null);
    setShowForm(false);
  };

  const handleTagToggle = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags.includes(tag)
        ? formData.tags.filter(t => t !== tag)
        : [...formData.tags, tag]
    });
  };

  const isAdmin = user.role === 'admin';

  return (
    <div className="max-w-7xl mx-auto p-6">
      {isAdmin && (
        <div className="mb-6">
          <button
            onClick={() => setShowForm(!showForm)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md ${buttonPrimary}`}
          >
            <Plus size={18} />
            Add New Dish
          </button>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">
              {editingItem ? (isAdmin ? 'Edit Dish' : 'Update Status') : 'Add New Dish'}
            </h2>
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            {/* For non-admin: only show status field */}
            {!isAdmin && editingItem ? (
              <>
                <div className="bg-gray-50 p-4 rounded-md mb-4">
                  <div className="text-sm text-gray-600 mb-2">
                    <strong>Dish:</strong> {editingItem.dish_name}
                  </div>
                  <div className="text-sm text-gray-600">
                    <strong>Category:</strong> {editingItem.category}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                  >
                    <option value="On">On</option>
                    <option value="Off">Off</option>
                  </select>
                </div>
              </>
            ) : (
              /* Admin: show all fields */
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    list="categories"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                    placeholder="Select or type new category"
                  />
                  <datalist id="categories">
                    {categories.map(cat => <option key={cat} value={cat} />)}
                  </datalist>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dish Name</label>
                  <input
                    type="text"
                    value={formData.dish_name}
                    onChange={(e) => setFormData({...formData, dish_name: e.target.value})}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                    placeholder="Enter dish name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                  >
                    <option value="On">On</option>
                    <option value="Off">Off</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Spice Level</label>
                  <select
                    value={formData.spice_level}
                    onChange={(e) => setFormData({...formData, spice_level: e.target.value})}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                  >
                    {spiceLevels.map(level => <option key={level} value={level}>{level}</option>)}
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                    rows="3"
                    placeholder="Enter dish description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleTagToggle(tag)}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          formData.tags.includes(tag)
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({...formData, cost: e.target.value})}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                    placeholder="0.00"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`px-6 py-2 rounded-md font-medium transition-colors disabled:bg-gray-400 ${buttonPrimary}`}
              >
                {loading ? 'Saving...' : (editingItem ? 'Update' : 'Add') + (isAdmin ? ' Dish' : ' Status')}
              </button>
              <button
                onClick={resetForm}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${buttonSecondary}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading && menuItems.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Loading menu items...</div>
        ) : menuItems.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No menu items yet. {isAdmin ? 'Click "Add New Dish" to get started!' : ''}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Dish Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Tags</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Spice</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Cost</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {menuItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-700">{item.category}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.dish_name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        item.status === 'On' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{item.description}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(item.tags || []).map(tag => (
                          <span key={tag} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.spice_level}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      ${Number(item.cost).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title={isAdmin ? "Edit dish" : "Update status"}
                        >
                          <Edit size={18} />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Delete dish"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Menu;